import { useCallback, useEffect, useMemo, useState } from 'react';
import { getDomainDisplayName } from '@app/shared/utils/domainDisplay.utils';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileLines,
  faCloudArrowUp,
  faShieldHalved,
  faChevronDown,
  faPen,
  faTrashCan,
  faChartPie,
  faCertificate,
  faPlay,
  faFilePdf
} from '@fortawesome/free-solid-svg-icons';
import FeatherIcon from 'feather-icons-react';
import { useDispatch } from 'react-redux';
import { setToast } from 'components/Toasts/data-access/toast-reducer';
import { useMainLayoutContext } from 'providers/MainLayoutProvider';
import {
  ATACDocument,
  ATACProject,
  ATACResultsResponse,
  getATACProject,
  getATACResults,
  getLatestCompletedRun,
  listATACDocuments
} from 'data-access/atac.api';
import { AnalysisTab, CertificateTab, DocumentsTab, ResultsTab } from './components';
import './ProjectDetails.css';

type NavItem = 'summary' | 'evidence-uploads' | 'analysis' | 'dashboard' | 'certificate';

const NAV_QUERY_PARAM = 'nav';

const isNavItem = (value: string | null): value is NavItem =>
  value === 'summary' ||
  value === 'evidence-uploads' ||
  value === 'analysis' ||
  value === 'dashboard' ||
  value === 'certificate';

const readNavFromSearchParams = (searchParams: URLSearchParams): NavItem => {
  const nav = searchParams.get(NAV_QUERY_PARAM);
  return isNavItem(nav) ? nav : 'summary';
};

const ATEPlusProjectDetails = () => {
  const { setContentClass } = useMainLayoutContext();
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();

  const [project, setProject] = useState<ATACProject | null>(null);
  const [documents, setDocuments] = useState<ATACDocument[]>([]);
  const [results, setResults] = useState<ATACResultsResponse | null>(null);
  const [activeNav, setActiveNav] = useState<NavItem>(() => readNavFromSearchParams(searchParams));
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadsExpanded, setUploadsExpanded] = useState(true);
  const [llmModelEvaluationExpanded, setLlmModelEvaluationExpanded] = useState(true);
  const [resultsExpanded, setResultsExpanded] = useState(true);
  const [llmApplicationEvaluationExpanded, setLlmApplicationEvaluationExpanded] = useState(false);

  const fetchProjectBundle = useCallback(async () => {
    const [projRes, docsRes, resRes] = await Promise.all([
      getATACProject(projectId!),
      listATACDocuments(projectId!),
      getATACResults(projectId!).catch(() => null)
    ]);
    return {
      project: projRes.data?.project ?? null,
      documents: docsRes.data?.documents ?? [],
      results: resRes?.data ?? null
    };
  }, [projectId]);

  const refreshData = useCallback(async () => {
    if (!projectId) return;
    const data = await fetchProjectBundle();
    setProject(data.project);
    setDocuments(data.documents);
    setResults(data.results);
  }, [projectId, fetchProjectBundle]);

  useEffect(() => {
    if (!projectId) {
      setError('Project ID is missing');
      setIsLoading(false);
      return;
    }

    localStorage.setItem('projectId', projectId);
    const userId = localStorage.getItem('userId') || '';
    if (!userId) {
      setError('User not logged in');
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    fetchProjectBundle()
      .then(data => {
        if (cancelled) return;
        if (!data.project) {
          setError('Project not found');
          setProject(null);
          return;
        }
        setProject(data.project);
        setDocuments(data.documents);
        setResults(data.results);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Failed to fetch ATE+ project:', err);
        setError('Failed to load project');
        setProject(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [projectId, fetchProjectBundle]);

  useEffect(() => {
    setContentClass('px-0 pt-navbar');
    return () => setContentClass('');
  }, [setContentClass]);

  useEffect(() => {
    const navFromUrl = readNavFromSearchParams(searchParams);
    setActiveNav(prev => (prev === navFromUrl ? prev : navFromUrl));
  }, [searchParams]);

  useEffect(() => {
    if (activeNav === 'analysis' || activeNav === 'dashboard' || activeNav === 'certificate') {
      setLlmModelEvaluationExpanded(true);
    }
    if (activeNav === 'dashboard' || activeNav === 'certificate') {
      setResultsExpanded(true);
    }
    if (activeNav === 'evidence-uploads') {
      setUploadsExpanded(true);
    }
  }, [activeNav]);

  const unlockRules = useMemo(() => {
    const runs = results?.runs ?? [];
    const hasDocuments = documents.length > 0;
    const hasCompletedRun = runs.some(r => r.status === 'completed');
    const latestCompleted = getLatestCompletedRun(runs);

    return {
      analysis: hasDocuments,
      dashboard: hasCompletedRun,
      certificate: latestCompleted?.status === 'completed'
    };
  }, [documents.length, results]);

  const showYetToBeImplementedToast = (action: 'Edit' | 'Delete') => {
    dispatch(
      setToast({
        show: true,
        title: `${action} project`,
        message: 'Yet to be implemented for ATE+.'
      })
    );
  };

  const handleNavSelect = useCallback(
    (item: NavItem) => {
      setActiveNav(item);

      const nextParams = new URLSearchParams(searchParams);
      if (item === 'summary') {
        nextParams.delete(NAV_QUERY_PARAM);
      } else {
        nextParams.set(NAV_QUERY_PARAM, item);
      }
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleRunCompleted = useCallback(async () => {
    await refreshData();
    handleNavSelect('dashboard');
  }, [refreshData, handleNavSelect]);

  const gradientStyle = {
    minHeight: '100vh',
    width: '100%',
    background: 'linear-gradient(293deg, rgb(239 243 255) 0%, rgb(255 255 255) 100%)',
    backgroundAttachment: 'fixed' as const,
    padding: '24px 16px',
    position: 'relative' as const
  };

  const isUploadsActive = activeNav === 'evidence-uploads';
  const isLlmModelEvaluationActive =
    activeNav === 'analysis' || activeNav === 'dashboard' || activeNav === 'certificate';
  const isResultsActive = activeNav === 'dashboard' || activeNav === 'certificate';

  const renderContent = () => {
    if (!projectId) return null;

    switch (activeNav) {
      case 'summary':
        return (
          <div className="csa-content-card" style={{ padding: '24px 32px' }}>
            <h5 className="fw-bold mb-3">LLM Model Evaluation</h5>
            {(project?.provider || project?.modelName) && (
              <div className="text-body-secondary mb-2">
                {project.provider && <div>Provider: {project.provider}</div>}
                {project.modelName && <div>Model: {project.modelName}</div>}
              </div>
            )}
            {project?.created_at && (
              <small className="text-muted">Created: {new Date(project.created_at).toLocaleString()}</small>
            )}
            {project?.projectId && (
              <div className="mt-2">
                <small className="text-muted">Project ID: {project.projectId}</small>
              </div>
            )}
          </div>
        );
      case 'evidence-uploads':
        return (
          <div className="csa-content-card" style={{ padding: '24px 32px' }}>
            <DocumentsTab
              projectId={projectId}
              project={project}
              documents={documents}
              onDocumentsChange={refreshData}
            />
          </div>
        );
      case 'analysis':
        return (
          <div className="csa-content-card" style={{ padding: '24px 32px' }}>
            <AnalysisTab
              projectId={projectId}
              documents={documents}
              results={results}
              onRunCompleted={handleRunCompleted}
            />
          </div>
        );
      case 'dashboard':
        return (
          <div className="csa-content-card" style={{ padding: '24px 32px' }}>
            <ResultsTab results={results} />
          </div>
        );
      case 'certificate':
        return (
          <div className="csa-content-card" style={{ padding: '24px 32px' }}>
            <CertificateTab projectId={projectId} results={results} onResultsUpdated={refreshData} />
          </div>
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div
        style={{
          ...gradientStyle,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <CircularProgress size={50} />
        <p className="text-muted mt-2">Loading project...</p>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div style={gradientStyle}>
        <div className="text-center py-5">
          <p className="text-danger">{error || 'Project not found'}</p>
          <Link to="/ate+/all-projects" className="btn btn-outline-primary">
            Back to All Projects
          </Link>
        </div>
      </div>
    );
  }

  const sidebarWidth = 280;

  return (
    <div className="csa-project-details" style={gradientStyle}>
      <aside
        className="csa-sidebar"
        style={{
          position: 'fixed',
          left: 0,
          top: 'var(--phoenix-navbar-top-height, 72px)',
          bottom: 0,
          width: sidebarWidth,
          backgroundColor: '#fff',
          borderRight: '1px solid #e5e7eb',
          boxShadow: '2px 0 8px rgba(0,0,0,0.04)',
          zIndex: 90,
          overflowY: 'auto',
          padding: '20px 12px'
        }}
      >
        <nav className="d-flex flex-column gap-0">
          <div
            className={`csa-nav-item ${activeNav === 'summary' ? 'active' : ''}`}
            onClick={() => handleNavSelect('summary')}
          >
            <div className="d-flex align-items-center gap-2">
              <FontAwesomeIcon
                icon={faFileLines}
                size="sm"
                className={activeNav === 'summary' ? 'text-primary' : 'text-muted'}
              />
              <span className="fw-semibold" style={{ fontSize: '14px' }}>
                Summary
              </span>
            </div>
          </div>

          <div className="mt-2">
            <div
              className={`csa-nav-item ${isUploadsActive ? 'active' : ''}`}
              onClick={() => setUploadsExpanded(!uploadsExpanded)}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <FontAwesomeIcon
                    icon={faCloudArrowUp}
                    size="sm"
                    className={isUploadsActive ? 'text-primary' : 'text-muted'}
                  />
                  <span className="fw-semibold" style={{ fontSize: '14px' }}>
                    Uploads
                  </span>
                </div>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  size="xs"
                  className={`csa-nav-expandable text-muted ${
                    uploadsExpanded ? 'csa-nav-expandable-expanded' : 'csa-nav-expandable-collapsed'
                  }`}
                />
              </div>
            </div>

            <div
              className={`csa-nav-submenu mt-1 ${
                uploadsExpanded ? 'csa-nav-submenu-expanded' : 'csa-nav-submenu-collapsed'
              }`}
            >
              <div className="csa-nav-submenu-inner">
                <div className="ps-2 border-start border-2 ms-2" style={{ borderColor: '#e5e7eb' }}>
                  <div
                    className={`csa-nav-subitem ${activeNav === 'evidence-uploads' ? 'active' : ''}`}
                    onClick={() => handleNavSelect('evidence-uploads')}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <FontAwesomeIcon
                        icon={faFilePdf}
                        size="sm"
                        className={activeNav === 'evidence-uploads' ? 'text-primary' : 'text-muted'}
                      />
                      <span>Evidence Uploads</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-2">
            <div
              className={`csa-nav-item ${isLlmModelEvaluationActive ? 'active' : ''}`}
              onClick={() => setLlmModelEvaluationExpanded(!llmModelEvaluationExpanded)}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <FontAwesomeIcon
                    icon={faShieldHalved}
                    size="sm"
                    className={isLlmModelEvaluationActive ? 'text-primary' : 'text-muted'}
                  />
                  <span className="fw-semibold" style={{ fontSize: '14px' }}>
                    LLM Model Evaluation
                  </span>
                </div>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  size="xs"
                  className={`csa-nav-expandable text-muted ${
                    llmModelEvaluationExpanded ? 'csa-nav-expandable-expanded' : 'csa-nav-expandable-collapsed'
                  }`}
                />
              </div>
            </div>

            <div
              className={`csa-nav-submenu mt-1 ${
                llmModelEvaluationExpanded ? 'csa-nav-submenu-expanded' : 'csa-nav-submenu-collapsed'
              }`}
            >
              <div className="csa-nav-submenu-inner">
                <div className="ps-2 border-start border-2 ms-2" style={{ borderColor: '#e5e7eb' }}>
                  <div
                    className={`csa-nav-subitem ${activeNav === 'analysis' ? 'active' : ''} ${
                      !unlockRules.analysis ? 'csa-nav-subitem-locked' : ''
                    }`}
                    onClick={() => handleNavSelect('analysis')}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <FontAwesomeIcon
                        icon={faPlay}
                        size="sm"
                        className={activeNav === 'analysis' ? 'text-primary' : 'text-muted'}
                      />
                      <span>Analysis</span>
                    </div>
                  </div>

                  <div
                    className={`csa-nav-subitem ${isResultsActive ? 'active' : ''}`}
                    onClick={e => {
                      e.stopPropagation();
                      setResultsExpanded(!resultsExpanded);
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center gap-2">
                        <FontAwesomeIcon
                          icon={faPlay}
                          size="sm"
                          className={isResultsActive ? 'text-primary' : 'text-muted'}
                        />
                        <span>Results</span>
                      </div>
                      <FontAwesomeIcon
                        icon={faChevronDown}
                        size="xs"
                        className={`csa-nav-expandable text-muted ${
                          resultsExpanded ? 'csa-nav-expandable-expanded' : 'csa-nav-expandable-collapsed'
                        }`}
                      />
                    </div>
                  </div>

                  <div
                    className={`csa-nav-submenu mt-1 ${
                      resultsExpanded ? 'csa-nav-submenu-expanded' : 'csa-nav-submenu-collapsed'
                    }`}
                  >
                    <div className="csa-nav-submenu-inner">
                      <div className="ps-2 border-start border-2 ms-2" style={{ borderColor: '#e5e7eb' }}>
                        <div
                          className={`csa-nav-subitem ${activeNav === 'dashboard' ? 'active' : ''} ${
                            !unlockRules.dashboard ? 'csa-nav-subitem-locked' : ''
                          }`}
                          onClick={() => handleNavSelect('dashboard')}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <FontAwesomeIcon
                              icon={faChartPie}
                              size="sm"
                              className={activeNav === 'dashboard' ? 'text-primary' : 'text-muted'}
                            />
                            <span>Dashboard</span>
                          </div>
                        </div>
                        <div
                          className={`csa-nav-subitem ${activeNav === 'certificate' ? 'active' : ''} ${
                            !unlockRules.certificate ? 'csa-nav-subitem-locked' : ''
                          }`}
                          onClick={() => handleNavSelect('certificate')}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <FontAwesomeIcon
                              icon={faCertificate}
                              size="sm"
                              className={activeNav === 'certificate' ? 'text-primary' : 'text-muted'}
                            />
                            <span>Certificate</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-2">
            <div
              className="csa-nav-item"
              onClick={() => setLlmApplicationEvaluationExpanded(!llmApplicationEvaluationExpanded)}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <FontAwesomeIcon icon={faShieldHalved} size="sm" className="text-muted" />
                  <span className="fw-semibold" style={{ fontSize: '14px' }}>
                    LLM Application Evaluation
                  </span>
                </div>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  size="xs"
                  className={`csa-nav-expandable text-muted ${
                    llmApplicationEvaluationExpanded ? 'csa-nav-expandable-expanded' : 'csa-nav-expandable-collapsed'
                  }`}
                />
              </div>
            </div>
          </div>
        </nav>
      </aside>

      <main style={{ marginLeft: sidebarWidth, minHeight: '100vh' }}>
        <div
          className="csa-content-card"
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
            margin: '24px 24px 24px 16px',
            overflow: 'hidden'
          }}
        >
          <div style={{ padding: '32px 32px 24px 32px', borderBottom: '1px solid #e9ecef' }}>
            <div className="d-flex align-items-start justify-content-between">
              <div>
                <h2 className="mb-0 fw-bold" style={{ fontSize: '28px', color: '#212529' }}>
                  LLM Model Evaluation
                </h2>
                <p className="text-muted mb-0 mt-1 small">ATE+ AI Trust Evaluation</p>
              </div>
              <div className="d-flex align-items-center gap-3 csa-details-actions">
                <Link
                  to={`/ate+/update-project/${encodeURIComponent(projectId || '')}`}
                  className="csa-details-action csa-details-action-edit d-flex align-items-center gap-2"
                  onClick={e => {
                    e.preventDefault();
                    showYetToBeImplementedToast('Edit');
                  }}
                >
                  <FontAwesomeIcon icon={faPen} style={{ fontSize: '14px' }} />
                  Edit
                </Link>
                <button
                  type="button"
                  className="csa-details-action csa-details-action-delete d-flex align-items-center gap-2 border-0 bg-transparent"
                  onClick={() => showYetToBeImplementedToast('Delete')}
                >
                  <FontAwesomeIcon icon={faTrashCan} style={{ fontSize: '14px' }} />
                  Delete
                </button>
              </div>
            </div>
            {project.domainName && (
              <div className="d-flex align-items-center gap-2 text-muted mt-2" style={{ fontSize: '14px' }}>
                <FeatherIcon icon="briefcase" size={16} />
                <span>{getDomainDisplayName(project.domainName)}</span>
              </div>
            )}
          </div>

          <div>{renderContent()}</div>
        </div>
      </main>
    </div>
  );
};

export default ATEPlusProjectDetails;
